import React, { useState } from 'react';
import { Row, Col, Form, Button, Alert } from 'react-bootstrap';
import { useNavigate, Link } from 'react-router-dom';
import { 
    UserPlus, 
    Shield, 
    Star, 
    CheckCircle2, 
    AlertTriangle, 
    User, 
    Mail, 
    Lock, 
    Eye, 
    EyeOff 
} from 'lucide-react';
import { register, validateEmail } from '../services/authService';
import { useAuth } from '../context/AuthContext';

const RegisterPage = () => {
    const { loginWithProvider } = useAuth();
    const [formData, setFormData] = useState({
        firstName: '',
        lastName: '',
        email: '',
        password: '',
        userTypeId: 2, // Default to Job Seeker (2)
        agreeTerms: false
    });
    const [userType, setUserType] = useState('jobseeker'); // 'jobseeker' or 'recruiter'
    const [errors, setErrors] = useState({});
    const [isLoading, setIsLoading] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [passwordStrength, setPasswordStrength] = useState(0);
    const [successMessage, setSuccessMessage] = useState('');
    const navigate = useNavigate();

    const handleSocialLogin = async (provider) => {
        setIsLoading(true);
        try {
            const selectedRole = userType === 'recruiter' ? 'Recruiter' : 'Job Seeker';
            await loginWithProvider(provider, selectedRole);
        } catch (err) {
            console.error('Social registration error:', err);
            setErrors({ general: err.message || 'OAuth authentication failed.' });
            setIsLoading(false);
        }
    };

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));

        // Clear field-specific errors
        if (errors[name]) {
            setErrors(prev => ({
                ...prev,
                [name]: ''
            }));
        }

        // Real-time email validation
        if (name === 'email' && value) {
            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            if (!emailRegex.test(value)) {
                setErrors(prev => ({
                    ...prev,
                    email: 'Please enter a valid email address'
                }));
            }
        }

        // Password strength validation
        if (name === 'password') {
            calculatePasswordStrength(value);
        }
    };

    const calculatePasswordStrength = (password) => {
        let strength = 0;
        
        if (password.length >= 6) strength++;
        if (password.length >= 8) strength++;
        if (/[a-z]/.test(password)) strength++;
        if (/[A-Z]/.test(password)) strength++;
        if (/[0-9]/.test(password)) strength++;
        if (/[^A-Za-z0-9]/.test(password)) strength++;
        
        setPasswordStrength(strength);
    };

    const getPasswordStrengthClass = () => {
        if (passwordStrength <= 2) return 'strength-weak';
        if (passwordStrength <= 3) return 'strength-fair';
        if (passwordStrength <= 4) return 'strength-good';
        return 'strength-strong';
    };

    const getPasswordStrengthText = () => {
        if (!formData.password) return 'Password must be 6-100 characters long';
        if (passwordStrength <= 2) return 'Weak password';
        if (passwordStrength <= 3) return 'Fair password';
        if (passwordStrength <= 4) return 'Good password';
        return 'Strong password';
    };

    const handleUserTypeSelect = (type) => {
        setUserType(type);
        setFormData(prev => ({
            ...prev,
            userTypeId: type === 'jobseeker' ? 2 : 1  // DB: 1=Recruiter, 2=Job Seeker
        }));
    };

    const validateForm = () => {
        const newErrors = {};

        // First Name validation
        if (!formData.firstName.trim()) {
            newErrors.firstName = 'First name is required';
        } else if (formData.firstName.length < 2) {
            newErrors.firstName = 'First name must be at least 2 characters long';
        }

        // Last Name validation
        if (!formData.lastName.trim()) {
            newErrors.lastName = 'Last name is required';
        } else if (formData.lastName.length < 2) {
            newErrors.lastName = 'Last name must be at least 2 characters long';
        }

        // Email validation
        if (!formData.email) {
            newErrors.email = 'Email is required';
        } else {
            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            if (!emailRegex.test(formData.email)) {
                newErrors.email = 'Please enter a valid email address';
            }
        }

        // Password validation
        if (!formData.password) {
            newErrors.password = 'Password is required';
        } else if (formData.password.length < 6) {
            newErrors.password = 'Password must be at least 6 characters long';
        } else if (formData.password.length > 100) {
            newErrors.password = 'Password must be less than 100 characters';
        }

        // Terms validation
        if (!formData.agreeTerms) {
            newErrors.agreeTerms = 'You must agree to the terms and conditions';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        console.log('Form submitted with data:', formData); // Debug log
        
        if (!validateForm()) {
            console.log('Form validation failed:', errors);
            return;
        }

        setIsLoading(true);
        setErrors({});

        try {
            // Verify email existence
            const emailCheck = await validateEmail(formData.email.trim());
            if (emailCheck && emailCheck.data === false) {
                setErrors({ email: 'Email does not exist' });
                setIsLoading(false);
                return;
            }

            const registrationData = {
                firstName: formData.firstName.trim(),
                lastName: formData.lastName.trim(),
                email: formData.email.trim(),
                password: formData.password,
                userTypeId: formData.userTypeId
            };

            console.log('Sending registration data:', registrationData); // Debug log

            const response = await register(registrationData);
            
            console.log('Registration successful:', response); // Debug log
            
            setSuccessMessage('Account created successfully! Please sign in to continue.');
            
            // Redirect to login after 2 seconds
            setTimeout(() => {
                navigate('/login');
            }, 2000);

        } catch (error) {
            console.error('Registration error:', error);
            
            if (error.response && error.response.data) {
                if (error.response.data.validationErrors) {
                    setErrors(error.response.data.validationErrors);
                } else if (error.response.data.message) {
                    setErrors({ general: error.response.data.message });
                } else {
                    setErrors({ general: 'Registration failed. Please try again.' });
                }
            } else if (error.message) {
                setErrors({ general: error.message });
            } else {
                setErrors({ general: 'Network error. Please check your connection and try again.' });
            }
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="auth-body">
            <div className="auth-container">
                <Row className="min-vh-100 g-0">
                    {/* Left Side - Branding */}
                    <Col lg={6} className="d-none d-lg-flex">
                        <div className="auth-brand-section">
                            <div className="auth-brand-overlay"></div>
                            <div className="auth-brand-content">
                                <div className="brand-logo-large">
                                    <h1>
                                        <span className="brand-name">Zpluse</span>{' '}
                                        <span className="brand-suffix">Jobs Finder</span>
                                    </h1>
                                    <p className="brand-tagline">
                                        Join Thousands of Professionals Finding Their Dream Jobs
                                    </p>
                                </div>
                                
                                <div className="auth-features">
                                    <div className="feature-item">
                                        <div className="feature-icon">
                                            <UserPlus size={24} />
                                        </div>
                                        <div className="feature-text">
                                            <h6>Quick Registration</h6>
                                            <p>Create your account in less than 2 minutes</p>
                                        </div>
                                    </div>
                                    <div className="feature-item">
                                        <div className="feature-icon">
                                            <Shield size={24} />
                                        </div>
                                        <div className="feature-text">
                                            <h6>Secure & Private</h6>
                                            <p>Your data is protected with enterprise-grade security</p>
                                        </div>
                                    </div>
                                    <div className="feature-item">
                                        <div className="feature-icon">
                                            <Star size={24} />
                                        </div>
                                        <div className="feature-text">
                                            <h6>Premium Experience</h6>
                                            <p>Access exclusive features and priority support</p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </Col>
                    
                    {/* Right Side - Register Form */}
                    <Col lg={6}>
                        <div className="auth-form-section">
                            <div className="auth-form-container">
                                {/* Mobile Brand Logo */}
                                <div className="mobile-brand d-lg-none text-center mb-4">
                                    <h3>
                                        <span className="brand-name">Zpluse</span>{' '}
                                        <span className="brand-suffix">Jobs Finder</span>
                                    </h3>
                                </div>
                                
                                <div className="auth-form-header">
                                    <h2 className="auth-title">Create Your Account</h2>
                                    <p className="auth-subtitle">Start your journey to find the perfect job today</p>
                                </div>
                                
                                {/* Success Message */}
                                {successMessage && (
                                    <div className="alert alert-success alert-custom alert-success d-flex align-items-center" role="alert">
                                        <CheckCircle2 className="me-2" size={18} />
                                        {successMessage}
                                    </div>
                                )}
                                
                                {/* General Error Message */}
                                {errors.general && (
                                    <div className="alert alert-danger alert-custom alert-error d-flex align-items-center" role="alert">
                                        <AlertTriangle className="me-2" size={18} style={{ minWidth: '18px', display: 'inline-block' }} />
                                        {errors.general}
                                    </div>
                                )}
                                
                                {/* Validation Errors Summary */}
                                {Object.keys(errors).length > 1 && (
                                    <div className="alert alert-danger alert-custom alert-error" role="alert">
                                        <h6 className="d-flex align-items-center">
                                            <AlertTriangle className="me-2" size={18} style={{ minWidth: '18px', display: 'inline-block' }} />
                                            Please correct the following errors:
                                        </h6>
                                        <ul className="mb-0 mt-2">
                                            {Object.entries(errors).map(([key, value]) => 
                                                key !== 'general' && (
                                                    <li key={key}>{value}</li>
                                                )
                                            )}
                                        </ul>
                                    </div>
                                )}
                                
                                {/* User Type Selection */}
                                <div className="user-type-selection">
                                    <Form.Label>I want to:</Form.Label>
                                    <div className="user-type-cards">
                                        <div 
                                            className={`user-type-card ${userType === 'jobseeker' ? 'active' : ''}`}
                                            onClick={() => handleUserTypeSelect('jobseeker')}
                                        >
                                            <div className="card-icon">👨‍💼</div>
                                            <h6>Find Jobs</h6>
                                            <p>Job Seeker</p>
                                        </div>
                                        <div 
                                            className={`user-type-card ${userType === 'recruiter' ? 'active' : ''}`}
                                            onClick={() => handleUserTypeSelect('recruiter')}
                                        >
                                            <div className="card-icon">🏢</div>
                                            <h6>Hire Talent</h6>
                                            <p>Recruiter</p>
                                        </div>
                                    </div>
                                </div>
                                
                                <Form onSubmit={handleSubmit} className="auth-form" noValidate>
                                    {/* First Name Field */}
                                    <Form.Group className="mb-3">
                                        <Form.Label>First Name</Form.Label>
                                        <div className="input-group">
                                            <span className="input-group-text">
                                                <User size={18} />
                                            </span>
                                            <Form.Control
                                                type="text"
                                                name="firstName"
                                                placeholder="Enter your first name"
                                                value={formData.firstName}
                                                onChange={handleInputChange}
                                                isInvalid={!!errors.firstName}
                                                isValid={formData.firstName && !errors.firstName && formData.firstName.length >= 2}
                                                className="modern-input"
                                                required
                                                maxLength={50}
                                            />
                                        </div>
                                        {errors.firstName && (
                                            <div className="invalid-feedback d-block">
                                                {errors.firstName}
                                            </div>
                                        )}
                                    </Form.Group>

                                    {/* Last Name Field */}
                                    <Form.Group className="mb-3">
                                        <Form.Label>Last Name</Form.Label>
                                        <div className="input-group">
                                            <span className="input-group-text">
                                                <User size={18} />
                                            </span>
                                            <Form.Control
                                                type="text"
                                                name="lastName"
                                                placeholder="Enter your last name"
                                                value={formData.lastName}
                                                onChange={handleInputChange}
                                                isInvalid={!!errors.lastName}
                                                isValid={formData.lastName && !errors.lastName && formData.lastName.length >= 2}
                                                className="modern-input"
                                                required
                                                maxLength={50}
                                            />
                                        </div>
                                        {errors.lastName && (
                                            <div className="invalid-feedback d-block">
                                                {errors.lastName}
                                            </div>
                                        )}
                                    </Form.Group>
                                    
                                    {/* Email Field */}
                                    <Form.Group className="mb-3">
                                        <Form.Label>Email Address</Form.Label>
                                        <div className="input-group">
                                            <span className="input-group-text">
                                                <Mail size={18} />
                                            </span>
                                            <Form.Control
                                                type="email"
                                                name="email"
                                                placeholder="Enter your email address"
                                                value={formData.email}
                                                onChange={handleInputChange}
                                                isInvalid={!!errors.email}
                                                isValid={formData.email && !errors.email && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)}
                                                className="modern-input"
                                                required
                                                maxLength={100}
                                            />
                                        </div>
                                        {errors.email && (
                                            <div className="invalid-feedback d-block">
                                                {errors.email}
                                            </div>
                                        )}
                                    </Form.Group>
                                    
                                    {/* Password Field */}
                                    <Form.Group className="mb-3">
                                        <Form.Label>Password</Form.Label>
                                        <div className="input-group">
                                            <span className="input-group-text">
                                                <Lock size={18} />
                                            </span>
                                            <Form.Control
                                                type={showPassword ? 'text' : 'password'}
                                                name="password"
                                                placeholder="Create a strong password"
                                                value={formData.password}
                                                onChange={handleInputChange}
                                                isInvalid={!!errors.password}
                                                isValid={formData.password && passwordStrength >= 3}
                                                className="modern-input"
                                                required
                                                minLength={6}
                                                maxLength={100}
                                            />
                                            <Button 
                                                variant="outline-secondary" 
                                                type="button"
                                                onClick={() => setShowPassword(!showPassword)}
                                                className="d-flex align-items-center"
                                            >
                                                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                                            </Button>
                                        </div>
                                        
                                        {/* Password Strength Indicator */}
                                        {formData.password && (
                                            <div className="password-strength">
                                                <div className={`password-strength-bar ${getPasswordStrengthClass()}`}></div>
                                            </div>
                                        )}
                                        
                                        <small className={`text-muted ${passwordStrength <= 2 ? 'text-danger' : passwordStrength <= 3 ? 'text-warning' : 'text-success'}`}>
                                            {getPasswordStrengthText()}
                                        </small>
                                        
                                        {errors.password && (
                                            <div className="invalid-feedback d-block">
                                                {errors.password}
                                            </div>
                                        )}
                                    </Form.Group>
                                    
                                    {/* Terms Checkbox */}
                                    <Form.Group className="mb-4">
                                        <Form.Check
                                            type="checkbox"
                                            name="agreeTerms"
                                            checked={formData.agreeTerms}
                                            onChange={handleInputChange}
                                            isInvalid={!!errors.agreeTerms}
                                            label={
                                                <>
                                                    I agree to the{' '}
                                                    <Link to="#" className="text-primary">Terms of Service</Link>
                                                    {' '}and{' '}
                                                    <Link to="#" className="text-primary">Privacy Policy</Link>
                                                </>
                                            }
                                            required
                                        />
                                        {errors.agreeTerms && (
                                            <div className="invalid-feedback d-block">
                                                {errors.agreeTerms}
                                            </div>
                                        )}
                                    </Form.Group>
                                    
                                    {/* Submit Button */}
                                    <Button
                                        type="submit"
                                        variant="primary"
                                        className="auth-btn w-100 mb-4 d-flex align-items-center justify-content-center"
                                        disabled={isLoading}
                                    >
                                        {isLoading ? (
                                            <div className="loading-spinner me-2" style={{ width: '1rem', height: '1rem' }}></div>
                                        ) : (
                                            <UserPlus className="me-2" size={18} />
                                        )}
                                        {isLoading ? 'Creating Account...' : 'Create Account'}
                                    </Button>

                                    {/* Social Logins */}
                                    <div className="social-auth-section mb-4">
                                        <div className="auth-divider mb-3">
                                            <span>Or register with</span>
                                        </div>
                                        
                                        <div className="d-flex gap-2">
                                            <Button
                                                type="button"
                                                variant="outline-secondary"
                                                className="w-100 d-flex align-items-center justify-content-center gap-2 py-2"
                                                onClick={() => handleSocialLogin('google')}
                                                disabled={isLoading}
                                            >
                                                <svg width="18" height="18" viewBox="0 0 24 24" className="me-1">
                                                    <path fill="#4285F4" d="M23.745 12.27c0-.7-.06-1.4-.19-2.07H12v3.9h6.69c-.29 1.5-1.13 2.77-2.4 3.61l3.71 2.87c2.17-2 3.84-5 3.84-8.31z"/>
                                                    <path fill="#34A853" d="M12 24c3.24 0 5.97-1.08 7.96-2.91l-3.71-2.87c-1.07.72-2.45 1.15-4.12 1.15-3.17 0-5.85-2.14-6.81-5.02L1.31 15.22l-1.01.78C2.28 20.35 6.75 24 12 24z"/>
                                                    <path fill="#FBBC05" d="M5.19 14.25c-.24-.72-.38-1.49-.38-2.25s.14-1.53.38-2.25V4.73L1.31 1.76l-.78 1.01C-.8 6.02-.8 11.98 0 16.5l5.19-2.25z"/>
                                                    <path fill="#EA4335" d="M12 4.75c1.77 0 3.35.61 4.6 1.8l3.44-3.44C17.96 1.19 15.24 0 12 0 6.75 0 2.28 3.65.53 8.01l4.66 3.61c.96-2.88 3.64-5.02 6.81-5.02z"/>
                                                </svg>
                                                Google
                                            </Button>
                                            <Button
                                                type="button"
                                                variant="outline-secondary"
                                                className="w-100 d-flex align-items-center justify-content-center gap-2 py-2"
                                                onClick={() => handleSocialLogin('linkedin_oidc')}
                                                disabled={isLoading}
                                            >
                                                <svg width="18" height="18" viewBox="0 0 24 24" fill="#0A66C2" className="me-1">
                                                    <path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.779-1.75-1.75s.784-1.75 1.75-1.75 1.75.779 1.75 1.75-.784 1.75-1.75 1.75zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z"/>
                                                </svg>
                                                LinkedIn
                                            </Button>
                                        </div>
                                    </div>

                                    <div className="auth-divider">
                                        <span>Already have an account?</span>
                                    </div>
                                    
                                    <div className="auth-footer text-center">
                                        <p className="mb-0">
                                            <Link to="/login" className="register-link">
                                                Sign In Instead
                                            </Link>
                                        </p>
                                    </div>
                                </Form>
                            </div>
                        </div>
                    </Col>
                </Row>
            </div>
        </div>
    );
};

export default RegisterPage;